import { css } from "@emotion/react";
import styled from "@emotion/styled";
import _ from "lodash";
import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Info } from "@livechat/design-system-icons";
import {
  Button,
  Icon as DSIcon,
  DesignToken,
  Heading,
  Loader,
  SpacingToken,
  Text,
  Tooltip,
} from "@livechat/design-system-react-components";

import imageSrc from "assets/img/welcome/import-data.png";
import { Icon, IconsEnum } from "components/icon";
import Flex from "components/layout/Flex";
import { SubscriptionPhase } from "constants/subscription-phase";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { getSubscriptionPhase } from "helpers/subscription";
import { LiveChatDataImportType } from "routes/settings/integrations/constants";
import { getItem as getSessionStoreItem, saveItem as saveSessionStoreItem } from "services/session-storage";
import { getNonViewersCount } from "store/entities/agents";
import { getBilling } from "store/entities/billing";
import {
  IntegrationsActions,
  selectIsFetchingLiveChatSelectiveImportDiff,
  selectIsManagedByShopify,
  selectIsShopifyTrial,
  selectLiveChatSelectiveImportDiff,
} from "store/entities/integrations";
import { selectManagedByPartner } from "store/entities/license";
import { selectCurrentSubscription, selectSeatsCount } from "store/entities/subscriptions";
import { selectIsDarkMode } from "store/features/app";
import { WelcomeTutorialViewActions } from "store/views/welcome-tutorial";
import { withVar } from "themes/helpers";

import { WelcomeTutorialRoutes } from "../helpers";

import { ImportOptionCard } from "./ImportOptionCard";
import TitleOnboardingStep from "./TitleOnboardingStep";

const SELECTED_IMPORTS_ITEM = "selected-imports";

export const ImportDataStep: FC = () => {
  const dispatch = useDispatch();

  const billing = useSelector(getBilling);
  const subscription = useSelector(selectCurrentSubscription);
  const seatsCount = useSelector(selectSeatsCount);
  const nonViewersCount = useSelector(getNonViewersCount);
  const importDiff = useSelector(selectLiveChatSelectiveImportDiff);
  const isFetchingImportDiff = useSelector(selectIsFetchingLiveChatSelectiveImportDiff);
  const isHandledByShopify = useSelector(selectIsManagedByShopify);
  const isShopifyTrial = useSelector(selectIsShopifyTrial);
  const isManagedByPartner = useSelector(selectManagedByPartner);
  const isDarkModeTheme = useSelector(selectIsDarkMode);

  const [selectedImports, setSelectedImports] = useState<LiveChatDataImportType[]>(
    getSessionStoreItem(SELECTED_IMPORTS_ITEM) || _.values(LiveChatDataImportType)
  );

  const subscriptionPhase = getSubscriptionPhase(subscription, billing);
  const agentsToImport = importDiff?.agents?.create || 0;
  const seatsToAdd = agentsToImport - (seatsCount - nonViewersCount);
  const shouldShowDisclaimer =
    (subscriptionPhase === SubscriptionPhase.Paid ||
      subscriptionPhase === SubscriptionPhase.PaidNoCC ||
      (isHandledByShopify && !isShopifyTrial) ||
      isManagedByPartner) &&
    seatsToAdd > 0;

  const noImportSelected = selectedImports.length === 0;
  const loading = isFetchingImportDiff;

  const isImportOptionSelected = (importOption: LiveChatDataImportType): boolean =>
    selectedImports.includes(importOption);

  const updateSelectedImports = (updatedImports: LiveChatDataImportType[]): void => {
    setSelectedImports(updatedImports);
    saveSessionStoreItem(SELECTED_IMPORTS_ITEM, updatedImports);
  };

  const handleSelectImportClick = (importOption: LiveChatDataImportType): void => {
    updateSelectedImports(
      isImportOptionSelected(importOption)
        ? selectedImports.filter(selectedImport => selectedImport !== importOption)
        : [...selectedImports, importOption]
    );
  };

  const finishTutorial = (): void => {
    dispatch(WelcomeTutorialViewActions.finish());
    trackEngagement("LC integration: Welcome screens finished", EventPlace.Onboarding);
  };

  const startImport = (): void => {
    trackEngagement("LC integration: Import data", EventPlace.Onboarding, {
      importedData: selectedImports.sort().join("+"),
      firstTime: true,
      eventPlace: "welcomeScreen",
    });

    dispatch(
      IntegrationsActions.startLiveChatSelectiveImport({
        types: selectedImports,
      })
    );
  };

  const handleNextButtonClick = (): void => {
    finishTutorial();

    if (!noImportSelected) {
      startImport();
    }
  };

  useEffect(() => {
    if (!importDiff) {
      dispatch(IntegrationsActions.fetchLiveChatImportDiff());
    }

    trackEngagement("LC integration: Welcome screens", EventPlace.Onboarding, {
      step: 4,
    });
  }, []);

  const NextButton = (
    <Button size="large" kind="primary" disabled={loading} onClick={handleNextButtonClick}>
      {noImportSelected ? "Continue" : "Import data"}
    </Button>
  );

  return (
    <TitleOnboardingStep
      currentStep={4}
      title={
        <Flex alignItems="center" gap="8px" marginBottom="8px">
          <Heading size="lg" customColor={withVar(DesignToken.ContentBasicPrimary)}>
            Import data from
          </Heading>
          <Icon
            name={isDarkModeTheme ? IconsEnum.LivechatFullLogoWhiteText : IconsEnum.LivechatFullLogoBlackText}
            width="106px"
            height="24px"
            css={css`
              padding-top: 6px;
            `}
          />
        </Flex>
      }
      subtitle="Choose the data you want to import."
      content={
        loading ? (
          <Flex alignContent="center" justifyContent="center" minHeight="300px">
            <Loader size="medium" />
          </Flex>
        ) : (
          <>
            <Flex gap={withVar(SpacingToken.Spacing2)} marginTop="24px" column>
              <ImportOptionCard
                type={LiveChatDataImportType.AgentsAndGroups}
                title="Agents and groups"
                onClick={handleSelectImportClick}
                selected={isImportOptionSelected(LiveChatDataImportType.AgentsAndGroups)}
                addons={
                  shouldShowDisclaimer && (
                    <Flex flexGrow={1} justifyContent="flex-end">
                      <Tooltip
                        triggerRenderer={
                          <DSIcon source={Info} customColor={withVar(DesignToken.ContentBasicPrimary)} />
                        }
                        placement="right"
                        kind="invert"
                        css={css`
                          max-width: 220px;
                        `}
                      >
                        Imported LiveChat users will keep their roles in HelpDesk and{" "}
                        <b>be billed per the chosen HelpDesk plan</b>.
                      </Tooltip>
                    </Flex>
                  )
                }
              />
              <ImportOptionCard
                type={LiveChatDataImportType.CannedResponses}
                title="Canned responses"
                onClick={handleSelectImportClick}
                selected={isImportOptionSelected(LiveChatDataImportType.CannedResponses)}
                addons={
                  <Flex flexGrow={1} justifyContent="flex-end">
                    <Tooltip
                      triggerRenderer={<DSIcon source={Info} customColor={withVar(DesignToken.ContentBasicPrimary)} />}
                      placement="right"
                      kind="invert"
                      css={css`
                        max-width: 220px;
                      `}
                    >
                      Only shared canned responses will be imported.
                    </Tooltip>
                  </Flex>
                }
              />
              <ImportOptionCard
                type={LiveChatDataImportType.Tags}
                title="Tags"
                onClick={handleSelectImportClick}
                selected={isImportOptionSelected(LiveChatDataImportType.Tags)}
              />
            </Flex>
            <ImportInfo size="sm">
              Importing may take time. Follow progress in HelpDesk settings.
              <br />
              All imported agents will get an agent/admin role in HelpDesk. This change is irreversible and will be done
              for the entire account. You can also import the data later in the settings.
            </ImportInfo>
          </>
        )
      }
      imageSource={imageSrc}
      nextButton={NextButton}
      backButtonRouteLink={WelcomeTutorialRoutes.LC_MANAGEABLE_TICKETS}
    />
  );
};

const ImportInfo = styled(Text)`
  margin-top: ${withVar(SpacingToken.Spacing2)};
  margin-bottom: 26px;
  color: ${withVar(DesignToken.ContentBasicDisabled)};
`;
